/**
 * Mithril abstractions and componentization.
 */
import { mount as mithrilMount } from 'mithril'
import { nodes } from './dom'
import { noop, makeSafeFn } from './fn'
import { queryString } from './window'
import { warn } from '../application/console'
import { registerUtils } from '../application/nm'
import { create as makeEmptyObj } from './object'

const currentlyMountedElementSet = new WeakSet()

export function unmount(rootNode) {
  return mithrilMount(rootNode, null)
}

function mountWithOptions(rootNode, opts) {
  const { component } = opts
  if (component === null) {
    return unmount(rootNode)
  }
  const componentOptions = component(Object.assign(opts, { rootNode }))
  if (currentlyMountedElementSet.has(rootNode)) {
    warn(`component with rootNode: "${rootNode.className}" already mounted`, { rootNode, opts })
  }
  currentlyMountedElementSet.add(rootNode)
  return mithrilMount(rootNode, componentOptions)
}

export function mount(opts = {}) {
  const { rootNode, rootSelector } = opts
  if (rootNode) {
    mountWithOptions(rootNode, opts)
  }
  if (rootSelector) {
    const rootNodes = nodes(rootSelector)
    if (rootNodes.length > 0) {
      for (const mountNode of rootNodes) {
        mountWithOptions(mountNode, opts)
      }
    } else if (queryString.debugMounts) {
      warn(
        `mountComponent called with rootNodeSelector: '${opts.rootSelector}' but didn't find it in DOM`,
        { opts }
      )
    }
  }
}

export function make(ctrl, viewClass = null) {
  return function componentMaker(options) {
    const componentName = ctrl.name.replace('Controller', '')
    if (!viewClass) {
      throw new Error(
        'Error in ' +
          ctrl.name +
          ' - pass view explicitly to component.make function. using component.make without explicitly passing view behavior has been disabled because of https://github.com/FTBpro/FTBpro/issues/1641'
      )
      // disabled until dynamic requires are made non recursive.
      // viewClass = require(`views/${ componentName }View`);
    }
    const viewInstance = viewClass.create()
    return {
      view: viewInstance.view.bind(viewInstance),
      controller: ctrl.bind(null, options),
      name: componentName,
    }
  }
}

export function mountAll(selectorComponentMap = {}) {
  for (const rootSelector in selectorComponentMap) {
    const component = selectorComponentMap[rootSelector]
    mount({ component, rootSelector })
  }
}

export const empty = makeSafeFn(make(noop, noop), { isMute: true })

export function areComponentsIdentical(comp1, comp2) {
  try {
    return comp1().name === comp2().name
  } catch (ex) {
    return false
  }
}

export const comp = Object.assign(makeEmptyObj(), {
  mount,
  unmount,
  mountAll,
  empty,
  make,
  areComponentsIdentical(comp1, comp2) {
    try {
      return comp1().name === comp2().name
    } catch (ex) {
      return false
    }
  },
})

registerUtils({ component: comp })



// WEBPACK FOOTER //
// ./provolution/app/views/provolution/jsCore/utils/component.js